/**
 * React context that hands the UserNotesDoc down to every component that
 * needs to read/write notes.
 *
 * The context value is produced by `<UserNotesDocProvider>` in
 * SyncProvider (mounted once per signed-in session). Consumers use the
 * `useDeckDoc(id)` hook to get a cheap per-deck view; they never touch
 * this context directly.
 *
 * A nullable provider is fine: `null` means "no user signed in yet", and
 * consumer hooks return null DeckDoc in that case — the same behaviour as
 * the old per-deck hook before the userId arrived.
 */

import { createContext } from 'react';

import type { UserNotesDoc } from './deck-doc';

export interface UserNotesDocContextValue {
  userDoc: UserNotesDoc | null;
  /**
   * True only once the doc has been reconciled with the server (or the
   * offline-fallback timeout has elapsed). Gate auto-creating writes on
   * this flag — calling `notes.fragment()` before this is true risks
   * racing with the server's sync-step2 and wiping notes via CRDT
   * tiebreaking.
   */
  synced: boolean;
  /** True once IndexedDB has replayed cached state into the Y.Doc. */
  idbSynced: boolean;
  /** True once the WebSocket Yjs sync handshake (sync-step2) has completed. */
  wsSynced: boolean;
}

export const UserNotesDocContext = createContext<UserNotesDocContextValue | null>(null);
